/**
 * Created by Ning Jiang on 11/22/2016.
 */

import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {NolimitPlugin} from "@nolimitcity/slot-launcher/bin/NolimitPlugin";
import {Logger, LogLevel} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {GameFlowController} from "@nolimitcity/slot-game/bin/flowcontrol/GameFlowController";
import {FlowState} from "@nolimitcity/slot-game/bin/flowcontrol/FlowState";
import {FlowStatePresentation} from "@nolimitcity/slot-game/bin/flowcontrol/FlowStatePresentation";
import {FontLoader} from "@nolimitcity/slot-launcher/bin/loader/FontLoader";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {OpenSans} from "@nolimitcity/slot-launcher/bin/loader/font/OpenSans";
import {AssetsConfig} from "@nolimitcity/slot-game/bin/asset/AssetsConfig";
import {AssetLoader} from "@nolimitcity/slot-game/bin/asset/AssetLoader";
import {ResourcesGroupName} from "@nolimitcity/slot-game/bin/asset/ResourcesGroupName";
import {TheBorderGameAssets} from "./TheBorderGameAssets";
import {TheBorderSound} from "./sound/TheBorderSound";
import {TheBorderBackgroundElements} from "./view/TheBorderBackgroundElements";
import {TheBorderWinPresentation} from "./gameflow/TheBorderWinPresentation";
import {TheBorderNormalResultPresentation} from "./gameflow/TheBorderNormalResultPresentation";
import {TheBorderIdleNormalPresentation} from "./gameflow/TheBorderIdleNormalPresentation";
import {ISymbolValues, TheBorderGameData} from "./server/TheBorderData";
import {TheBorderModel} from "./TheBorderModel";
import {TheBorderAllReelsView} from "./view/reels/TheBorderAllReelsView";
import {TheBorderNormalBetPresentation} from "./gameflow/TheBorderNormalBetPresentation";
import {TheBorderFreeSpinWinPresentation} from "./gameflow/freespin/TheBorderFreeSpinWinPresentation";
import {TheBorderFreeSpinIdlePresentation} from "./gameflow/freespin/TheBorderFreeSpinIdlePresentation";
import {TheBorderFreeSpinBetPresentation} from "./gameflow/freespin/TheBorderFreeSpinBetPresentation";
import {TheBorderFreeSpinResultPresentation} from "./gameflow/freespin/TheBorderFreeSpinResultPresentation";
import {TheBorderAvalancheIdlePresentation} from "./gameflow/avalanche/TheBorderAvalancheIdlePresentation";
import {TheBorderAvalancheBetPresentation} from "./gameflow/avalanche/TheBorderAvalancheBetPresentation";
import {
    TheBorderAvalancheFillUpReelsPresentation
} from "./gameflow/avalanche/TheBorderAvalancheFillUpReelsPresentation";
import {TheBorderGameMode} from "./server/TheBorderGameMode";
import {TheBorderFreeSpinIntroView} from "./view/freespin/TheBorderFreeSpinIntroView";
import {ResourceCreator} from "./resourcecreator/ResourceCreator";
import {TheBorderTotalWinView} from "./view/TheBorderTotalWinView";
import {TheBorderBigWinView} from "./view/TheBorderBigWinView";
import {TheBorderXSplitView} from "./view/TheBorderXSplitView";
import {TheBorderWinMultiplierView} from "./view/TheBorderWinMultiplierView";
import {TheBorderXBombView} from "./view/TheBorderXBombView";
import {TheBorderClusterWinView} from "./view/TheBorderClusterWinView";
import {TheBorderXNudgeView} from "./view/TheBorderXNudgeView";
import {TheBorderXNudgeSymbols} from "./pooling/TheBorderXNudgeSymbols";
import {TheBorderSymbols} from "./pooling/TheBorderSymbols";
import {TheBorderXBombSymbols} from "./pooling/TheBorderXBombSymbols";
import {TheBorderXSplitSymbols} from "./pooling/TheBorderXSplitSymbols";
import {TheBorderXWaysSymbols} from "./pooling/TheBorderXWaysSymbols";
import {TheBorderSymbolOnTopView} from "./view/TheBorderSymbolOnTopView";
import {TheBorderGPUPushView} from "./view/TheBorderGPUPushView";
import {TheBorderScatterSymbols} from "./pooling/TheBorderScatterSymbols";
import {TheBorderBlackOverlayView} from "./view/TheBorderBlackOverlayView";
import {TheBorderXRipButtonView} from "./view/TheBorderXRipButtonView";
import {TheBorderSuperFSIntroView} from "./view/superfreespin/TheBorderSuperFSIntroView";
import {TheBorderSuperFSBetPresentation} from "./gameflow/superfreespin/TheBorderSuperFSBetPresentation";
import {TheBorderSuperFSIdlePresentation} from "./gameflow/superfreespin/TheBorderSuperFSIdlePresentation";
import {TheBorderSuperFSWinPresentation} from "./gameflow/superfreespin/TheBorderSuperFSWinPresentation";
import {TheBorderFSToSuperFSIntroView} from "./view/TheBorderFSToSuperFSIntroView";
import {TheBorderOutroView} from "./view/base/TheBorderOutroView";
import {TheBorderXRipBlastView} from "./view/TheBorderXRipBlastView";
import {TheBorderMaxWinView} from "./view/TheBorderMaxWinView";
import {TheBorderSwooshAnimationView} from "./view/TheBorderSwooshAnimationView";
import {TheBorderXRipReelHighlighter} from "./view/TheBorderXRipReelHighlighter";
import {TemplateAsset, TemplateLoader} from "@nolimitcity/slot-launcher/bin/loader/TemplateLoader";
import {TheBorderXWaysView} from "./view/TheBorderXWaysView";
import {TheBorderIntroPanView} from "./view/TheBorderIntroPanView";
import {TheBorderReelHighlighter} from "./view/TheBorderReelHighlighter";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {APIEvent} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";

export class TheBorder extends SlotGame {
    public static model: TheBorderModel;
    public static assetLoader: AssetLoader;
    public static soundPlayer: TheBorderSound;
    public static backgroundElements: TheBorderBackgroundElements;
    public static gameFlowController: GameFlowController;
    public static symbols: TheBorderSymbols;
    public static scatterSymbols: TheBorderScatterSymbols;
    public static xWaysSymbols: TheBorderXWaysSymbols;
    public static xNudgeSymbols: TheBorderXNudgeSymbols;
    public static xSplitSymbols: TheBorderXSplitSymbols;
    public static xBombSymbols: TheBorderXBombSymbols;
    public static allReelsView: TheBorderAllReelsView;
    public static xSplitView: TheBorderXSplitView;
    public static avalancheFillUpReels: TheBorderAvalancheFillUpReelsPresentation;
    public static fsIntroView: TheBorderFreeSpinIntroView;
    public static outroView: TheBorderOutroView;
    public static superFSIntroView: TheBorderSuperFSIntroView;
    public static fsToSuperFSIntroView: TheBorderFSToSuperFSIntroView;
    public static totalWinView: TheBorderTotalWinView;
    public static bigWinView: TheBorderBigWinView;
    public static winMultiplierView: TheBorderWinMultiplierView;
    public static xBombView: TheBorderXBombView;
    public static clusterWinView: TheBorderClusterWinView;
    public static xNudgeView: TheBorderXNudgeView;
    public static symbolsOnTopView: TheBorderSymbolOnTopView;
    public static gpuPushView: TheBorderGPUPushView;
    public static winPres: TheBorderWinPresentation;
    public static blackOverlay: TheBorderBlackOverlayView;
    public static xRipButton: TheBorderXRipButtonView;
    public static xRipBlast: TheBorderXRipBlastView;
    public static maxWin: TheBorderMaxWinView;
    public static swooshAnimation: TheBorderSwooshAnimationView;
    public static xRipReelHighlighter: TheBorderXRipReelHighlighter;
    public static xWaysView: TheBorderXWaysView;
    public static introPanView: TheBorderIntroPanView;

    public static isInFS: boolean = false;
    public static buyFeatureName: string = "";
    public static IS_BOOSTED_BET_ON: boolean = false;
    public static wildSymbolNames: string[] = ["X_SPLIT", "X_SPLIT_SPENT", "X_BOMB", "WR", "X_SPLIT_H", "X_SPLIT_SPENT_H", "X_BOMB_H", "WR_H"];
    public static SYMBOLS_NOT_EXPLODING: string[] = ["BN", "BN_H", "WR", "WR_H"];

    private _defaultSymbolValues: ISymbolValues = {
        "L1": {3: 5, 4: 10, 5: 20},
        "L2": {3: 5, 4: 10, 5: 20},
        "L3": {3: 4, 4: 8, 5: 15},
        "M1": {3: 15, 4: 30, 5: 80},
        "M2": {3: 10, 4: 20, 5: 60},
        "M3": {3: 9, 4: 18, 5: 40},
        "M4": {3: 8, 4: 16, 5: 25},
        "M5": {3: 7, 4: 14, 5: 25}
    }
    public static reelHighlighter: TheBorderReelHighlighter;
    public static isActionSpinsActive:boolean = false;

    constructor() {
        super();
    }

    public static isWinBelowStake(): boolean {
        return this.apiPlugin.gameClientConfiguration.belowStakeWinRestriction && (this.model.data.accumulatedRoundWin <= this.getBuyFeaturePrice());
    }

    public static getBuyFeaturePrice(): number {
        const betLevel: string = TheBorder.apiPlugin.freeBets.hasFreeBets() ? TheBorder.apiPlugin.freeBets.getBet() : TheBorder.apiPlugin.betLevel.getLevel();
        let price: number = TheBorder.model.gameData?.playedBetValue || parseFloat(betLevel);
        if (this.buyFeatureName) {
            for (let index: number = 0; index < TheBorder.model.initData.featureBuyTimesBetValue.length; index++) {
                if (TheBorder.model.initData.featureBuyTimesBetValue[index].name === this.buyFeatureName) {
                    price *= TheBorder.model.initData.featureBuyTimesBetValue[index].price;
                    break;
                }
            }
        }
        return MathHelper.ceilToDecimals(price, 2);
    }

    public static checkXRipEffect(): boolean {
        return TheBorder.model.gameData.mode === TheBorderGameMode.NORMAL_AVALANCHE && TheBorder.model.gameData.nextMode === TheBorderGameMode.NORMAL &&
            TheBorder.IS_BOOSTED_BET_ON && TheBorder.model.gameData.accumulatedRoundWin === 0 && TheBorder.model.previousAccumulatedRoundWin > 0;
    }

    private static createViewsAndComponents() {
        //Non views
        TheBorder.soundPlayer = new TheBorderSound();

        TheBorder.gpuPushView = new TheBorderGPUPushView();
        TheBorder.symbolsOnTopView = new TheBorderSymbolOnTopView();
        TheBorder.allReelsView = new TheBorderAllReelsView();

        TheBorder.symbols.init();
        TheBorder.xWaysSymbols.init();
        TheBorder.scatterSymbols.init();
        TheBorder.xNudgeSymbols.init();
        TheBorder.xSplitSymbols.init();
        TheBorder.xBombSymbols.init();
        TheBorder.allReelsView.init();

        //Static views
        TheBorder.backgroundElements = new TheBorderBackgroundElements();
        TheBorder.backgroundElements.setMode(TheBorder.model.data.mode);
        TheBorder.blackOverlay = new TheBorderBlackOverlayView();
        TheBorder.xSplitView = new TheBorderXSplitView();
        TheBorder.fsIntroView = new TheBorderFreeSpinIntroView();
        TheBorder.outroView = new TheBorderOutroView();
        TheBorder.superFSIntroView = new TheBorderSuperFSIntroView();
        TheBorder.fsToSuperFSIntroView = new TheBorderFSToSuperFSIntroView();
        TheBorder.totalWinView = new TheBorderTotalWinView();
        TheBorder.bigWinView = new TheBorderBigWinView();
        TheBorder.winMultiplierView = new TheBorderWinMultiplierView();
        TheBorder.xBombView = new TheBorderXBombView();
        TheBorder.clusterWinView = new TheBorderClusterWinView();
        TheBorder.xNudgeView = new TheBorderXNudgeView();
        TheBorder.xRipButton = new TheBorderXRipButtonView();
        TheBorder.xRipBlast = new TheBorderXRipBlastView();
        TheBorder.maxWin = new TheBorderMaxWinView();
        TheBorder.swooshAnimation = new TheBorderSwooshAnimationView();
        TheBorder.xRipReelHighlighter = new TheBorderXRipReelHighlighter();
        TheBorder.reelHighlighter = new TheBorderReelHighlighter();
        TheBorder.xWaysView = new TheBorderXWaysView();
        TheBorder.introPanView = new TheBorderIntroPanView();

        NolimitApplication.addLayerAt(TheBorder.xRipBlast.name, TheBorder.xRipBlast, 0);
        NolimitApplication.addLayerAt(TheBorder.maxWin.name, TheBorder.maxWin, 0);
        NolimitApplication.addLayerAt(TheBorder.introPanView.name, TheBorder.introPanView, 0);
        NolimitApplication.addLayerAt(TheBorder.fsToSuperFSIntroView.name, TheBorder.fsToSuperFSIntroView, 0);
        NolimitApplication.addLayerAt(TheBorder.superFSIntroView.name, TheBorder.superFSIntroView, 0);
        NolimitApplication.addLayerAt(TheBorder.fsIntroView.name, TheBorder.fsIntroView, 0);
        NolimitApplication.addLayerAt(TheBorder.outroView.name, TheBorder.outroView, 0);
        NolimitApplication.addLayerAt(TheBorder.swooshAnimation.name, TheBorder.swooshAnimation, 0);
        NolimitApplication.addLayerAt(TheBorder.totalWinView.name, TheBorder.totalWinView, 0);
        NolimitApplication.addLayerAt(TheBorder.bigWinView.name, TheBorder.bigWinView, 0);
        NolimitApplication.addLayerAt(TheBorder.blackOverlay.name, TheBorder.blackOverlay, 0);
        NolimitApplication.addLayerAt(TheBorder.symbolsOnTopView.name, TheBorder.symbolsOnTopView, 0);
        NolimitApplication.addLayerAt(TheBorder.clusterWinView.name, TheBorder.clusterWinView, 0);
        NolimitApplication.addLayerAt(TheBorder.winMultiplierView.name, TheBorder.winMultiplierView, 0);
        NolimitApplication.addLayerAt(TheBorder.xNudgeView.name, TheBorder.xNudgeView, 0);
        NolimitApplication.addLayerAt(TheBorder.xBombView.name, TheBorder.xBombView, 0);
        NolimitApplication.addLayerAt(TheBorder.xSplitView.name, TheBorder.xSplitView, 0);
        NolimitApplication.addLayerAt(TheBorder.reelHighlighter.name, TheBorder.reelHighlighter, 0);
        NolimitApplication.addLayerAt(TheBorder.xRipReelHighlighter.name, TheBorder.xRipReelHighlighter, 0);
        NolimitApplication.addLayerAt(TheBorder.xWaysView.name, TheBorder.xWaysView, 0);
        NolimitApplication.addLayerAt(TheBorder.allReelsView.name, TheBorder.allReelsView, 0);
        NolimitApplication.addLayerAt(TheBorder.xRipButton.name, TheBorder.xRipButton, 0);
        NolimitApplication.addLayerAt(TheBorder.backgroundElements.name, TheBorder.backgroundElements, 0);
        NolimitApplication.addLayerAt(TheBorder.gpuPushView.name, TheBorder.gpuPushView, 0);


        TheBorder.apiPlugin.events.on(APIEvent.SELECTED_FEATURE_BET_CHANGED, ()=>{
            TheBorder.xRipButton.updateBoostBtnState();
        });

        TheBorder.apiPlugin.events.on(APIEvent.ACTION_SPINS_IS_ACTIVE,(data:any)=>{
            if (data === true){
                TheBorder.isActionSpinsActive = true;
            } else {
                TheBorder.isActionSpinsActive = false;
            }
        });



    }

    private static getSymbolString(key: string, data: { [numSymbols: number]: number }): string {
        return `
            <div class="symbol">
            <img src="{{gamePath}}screens/images/symbols/${key}.png">
            <div class="payout-group">
                <div class="payout">
                    <span style="margin-left: 1.19em ;padding-right:0.5em" class="number">160+</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[21] * 0.05}">${data[21] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: -0.28em" class="number">120-159</span>
                    <span style="margin-left: 0.1em"  class="multiplier" data-multiplier="${data[20] * 0.05}">${data[20] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: -0.28em" class="number">100-119</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[19] * 0.05}">${data[19] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">85-99</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[18] * 0.05}">${data[18] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">60-84</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[17] * 0.05}">${data[17] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">45-59</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[16] * 0.05}">${data[16] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">30-44</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[15] * 0.05}">${data[15] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">25-29</span>
                    <span style="margin-left: 0.1em"  class="multiplier" data-multiplier="${data[14] * 0.05}">${data[14] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">20-24</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[13] * 0.05}">${data[13] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">15-19</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[12] * 0.05}">${data[12] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em ;padding-right:0.6em" class="number">12-14</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[11] * 0.05}">${data[11] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 0.75em;padding-right:0.6em" class="number">10-11</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[10] * 0.05}">${data[10] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">9</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[9] * 0.05}">${data[9] * 0.05}</span>
                </div>
                <div class="payout">
                    <span  style="margin-left: 2.38em ;padding-right:1em" class="number">8</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[8] * 0.05}">${data[8] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">7</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[7] * 0.05}">${data[7] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">6</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[6] * 0.05}">${data[6] * 0.05}</span>
                </div>
                <div class="payout">
                    <span style="margin-left: 2.38em ;padding-right:1em" class="number">5</span>
                    <span style="margin-left: 0.1em" class="multiplier" data-multiplier="${data[5] * 0.05}">${data[5] * 0.05}</span>
                </div>
            </div>
            </div>
        `;
    }

    public getNoWinGameData(): any {
        return {
            "reels": [
                [
                    "L1",
                    "M1",
                    "L3",
                    "M1",
                    "M1",
                    "L2"
                ],
                [
                    "M2",
                    "L1",
                    "L2",
                    "L2",
                    "M1",
                    "M3"
                ],
                [
                    "M3",
                    "L1",
                    "L3",
                    "L3",
                    "M2",
                    "L2"
                ],
                [
                    "M4",
                    "L1",
                    "L2",
                    "L3",
                    "L1",
                    "M4"
                ],
                [
                    "M1",
                    "L3",
                    "M2",
                    "M3",
                    "M4",
                    "M2"
                ],
                [
                    "L1",
                    "L2",
                    "L3",
                    "M1",
                    "M2",
                    "M3"
                ]
            ],
            "evaluatedArea": [
                [
                    "L1",
                    "M1",
                    "L3",
                    "M1",
                    "M1",
                    "L2"
                ],
                [
                    "M2",
                    "L1",
                    "L2",
                    "L2",
                    "M1",
                    "M3"
                ],
                [
                    "M3",
                    "L1",
                    "L3",
                    "L3",
                    "M2",
                    "L2"
                ],
                [
                    "M4",
                    "L1",
                    "L2",
                    "L3",
                    "L1",
                    "M4"
                ],
                [
                    "M1",
                    "L3",
                    "M2",
                    "M3",
                    "M4",
                    "M2"
                ],
                [
                    "L1",
                    "L2",
                    "L3",
                    "M1",
                    "M2",
                    "M3"
                ]
            ],
            "betClusterWins": [],
            "thisSpinWinnings": 0,
            "accumulatedRoundWin": 0,
            "playedBetValue": 1,
            "reelsNextSpin": "BASE_REELSET",
            "mode": "NORMAL",
            "nextMode": "NORMAL",
            "freespinTriggeredThisSpin": false,
            "numberOfFreespinsPlayed": 0,
            "freespinsLeft": 0,
            "scatterPositionsEnteringFreespin": [
                0,
                0,
                0,
                0,
                0,
                0
            ],
            "scatterPositions": [
                0,
                0,
                0,
                0,
                0,
                0
            ],
            "allWinPositions": [
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ]
            ],
            "explodePositions": [
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ],
                [
                    false,
                    false,
                    false,
                    false,
                    false,
                    false
                ]
            ],
            "allSizesBeforeCollapse": [
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ],
                [
                    1,
                    1,
                    1,
                    1,
                    1,
                    1
                ]
            ],
            "size4xWay": false,
            "numberOfScatters": 0,
            "totalSpinRoundWinnings": 0,
            "enhancedBet": false,
            "wasFeatureBuy": false,
            "brokeBank": false,
            "currentMultiplier": 1,
            "multiplierNextSpin": 1,
            "xWayLevel": 0,
            "bonusLevel": 0
        };
    }

    public init(): Promise<NolimitPlugin> {
        return super.init().then((value: NolimitPlugin) => {
            TheBorder.model = new TheBorderModel(SlotGame.apiPlugin, true);
            TheBorder.gameFlowController = new GameFlowController(SlotGame.apiPlugin, TheBorder.model);
            TheBorder.gameFlowController.flowStateLogger.level = LogLevel.DEV;

            TheBorder.symbols = new TheBorderSymbols();
            TheBorder.xWaysSymbols = new TheBorderXWaysSymbols();
            TheBorder.scatterSymbols = new TheBorderScatterSymbols();
            TheBorder.xNudgeSymbols = new TheBorderXNudgeSymbols();
            TheBorder.xSplitSymbols = new TheBorderXSplitSymbols();
            TheBorder.xBombSymbols = new TheBorderXBombSymbols();


            //Main game
            TheBorder.winPres = new TheBorderWinPresentation();
            const idleNormal: TheBorderIdleNormalPresentation = new TheBorderIdleNormalPresentation();
            const reelStart: TheBorderNormalBetPresentation = new TheBorderNormalBetPresentation();
            const normalResult: TheBorderNormalResultPresentation = new TheBorderNormalResultPresentation();

            //Free spin
            const freeSpinWin: TheBorderFreeSpinWinPresentation = new TheBorderFreeSpinWinPresentation();
            const freeSpinIdle: TheBorderFreeSpinIdlePresentation = new TheBorderFreeSpinIdlePresentation();
            const freeSpinReelStart: TheBorderFreeSpinBetPresentation = new TheBorderFreeSpinBetPresentation();
            const freeSpinResult: TheBorderFreeSpinResultPresentation = new TheBorderFreeSpinResultPresentation();

            //Super FS
            const superFSIdle: TheBorderSuperFSIdlePresentation = new TheBorderSuperFSIdlePresentation();
            const superFSReelStart: TheBorderSuperFSBetPresentation = new TheBorderSuperFSBetPresentation();
            const superFSWin: TheBorderSuperFSWinPresentation = new TheBorderSuperFSWinPresentation();

            const avalancheIdle: TheBorderAvalancheIdlePresentation = new TheBorderAvalancheIdlePresentation();
            const avalancheStart: TheBorderAvalancheBetPresentation = new TheBorderAvalancheBetPresentation();
            TheBorder.avalancheFillUpReels = new TheBorderAvalancheFillUpReelsPresentation();

            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.NORMAL, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, idleNormal],
                [FlowState.BET, reelStart],
                [FlowState.RESULT, normalResult],
                [FlowState.WIN, TheBorder.winPres]
            ]));
            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.NORMAL_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, avalancheIdle],
                [FlowState.BET, avalancheStart],
                [FlowState.RESULT, TheBorder.avalancheFillUpReels],
                [FlowState.WIN, TheBorder.winPres]
            ]));
            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.FREESPIN, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, freeSpinIdle],
                [FlowState.BET, freeSpinReelStart],
                [FlowState.RESULT, freeSpinResult],
                [FlowState.WIN, freeSpinWin]
            ]));
            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.FREESPIN_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, avalancheIdle],
                [FlowState.BET, avalancheStart],
                [FlowState.RESULT, TheBorder.avalancheFillUpReels],
                [FlowState.WIN, freeSpinWin]
            ]));
            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.SUPER_FREESPIN, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, superFSIdle],
                [FlowState.BET, superFSReelStart],
                [FlowState.RESULT, freeSpinResult],
                [FlowState.WIN, superFSWin]
            ]));
            TheBorder.gameFlowController.addGameModeFlowStatePresentationMap(TheBorderGameMode.SUPER_FREESPIN_AVALANCHE, new Map<FlowState, FlowStatePresentation>([
                [FlowState.IDLE, avalancheIdle],
                [FlowState.BET, avalancheStart],
                [FlowState.RESULT, TheBorder.avalancheFillUpReels],
                [FlowState.WIN, superFSWin]
            ]));
            return value;
        });
    }

    public getReady(): Promise<NolimitPlugin> {
        //Show intro and load game assets
        return super.getReady().then((value: NolimitPlugin) => {

            const fontLoader = new FontLoader(NolimitApplication.resourcePath);
            fontLoader.add(OpenSans.ITALIC_600);
            fontLoader.add(OpenSans.NORMAL_300);
            fontLoader.add(OpenSans.NORMAL_400);
            fontLoader.add(OpenSans.NORMAL_700);

            AssetsConfig.addToAssetsConfig(TheBorderGameAssets.RESOURCES_GROUPS);
            TheBorder.assetLoader = new AssetLoader(TheBorder.apiPlugin, NolimitApplication.resourcePath + "/resources/");

            const promises = Promise.all([fontLoader.load(), TheBorder.assetLoader.load(ResourcesGroupName.MAIN)]);

            return promises.then((values: any) =>
                ResourceCreator.createResources().then(() => {
                    return value;
                })
            ).then((values: any) => {
                    TheBorder.createViewsAndComponents();
                    return values;
                }
            );
        });
    }

    public getReadyToStart(): Promise<NolimitPlugin> {
        //Game assets loaded, waiting for user to start game.
        return super.getReadyToStart().then((value: NolimitPlugin) => {
            return value;
        });
    }

    public start(): Promise<NolimitPlugin> {
        //User has pressed continue and the game starts.
        TheBorder.gpuPushView.cleanup();
        return super.start().then((value: NolimitPlugin) => {
            TheBorder.gameFlowController.start();
            TheBorder.soundPlayer.switchMusic(TheBorder.model.data.mode); //#318
            TheBorder.soundPlayer.playSound(TheBorderSound.FS_CONTINUE_START); //#318
            return value;
        });
    }

    public getPaytable(): Promise<string> {
        const templateLoader: TemplateLoader = new TemplateLoader(SlotGame.apiPlugin.resources.getStaticRoot());

        templateLoader.add({
            name: "Paytable",
            url: "nolimit/screens/paytable.mustache"
        });

        let symbolDataValueData: ISymbolValues = this._defaultSymbolValues;
        if (TheBorder.model.initData.symbolValues) {
            symbolDataValueData = TheBorder.model.initData.symbolValues;
            Logger.logDev("Using symbolValues from INIT data");
        } else {
            Logger.logDev("Using default symbolValues");
        }

        return templateLoader.load().then((assets: TemplateAsset[]) => {
            for (let asset of assets) {
                if (asset.name === "Paytable" && asset.loadedData) {
                    for (let key in symbolDataValueData) {
                        asset.loadedData = asset.loadedData.replace(`{{${key}}}`, TheBorder.getSymbolString(key, symbolDataValueData[key]))
                    }
                    return asset.loadedData;
                }
            }
            throw new Error("TheBorder could not load Paytable");
        });
    }
}